import { useDeleteDocument, useDocument, usePatchDocument, useRooms } from "@mimir/api-client"
import { createFileRoute, useRouter } from "@tanstack/react-router"
import "@blocknote/core/fonts/inter.css"
import { useCreateBlockNote } from "@blocknote/react"
import { BlockNoteView } from "@blocknote/shadcn"
import "@blocknote/shadcn/style.css"
import { EditorFormattingToolbar } from "@/components/editor-toolbar"
import { editorSchema } from "@/lib/editor-schema"
import { toast } from "sonner"
import { ChevronLeft, CircleAlertIcon, Dot, Pin, Trash2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog"
import { formatDistanceToNow } from "date-fns"
import { useState, useEffect, useRef } from "react"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Empty, EmptyDescription, EmptyTitle } from "@/components/ui/empty"
import { TagInput } from "@/components/tag-input"
import { cn } from "@/lib/utils"
import { useTheme } from "@/components/theme-provider"

export const Route = createFileRoute("/_app/mind-palace/$documentId")({
  component: DocumentDetailPage,
})

const CONTENT_TYPE_LABELS: Record<string, string> = {
  text: "Text",
  file: "File",
  link: "Link",
  note: "Note",
  audio: "Audio",
  image: "Image",
}

function DocumentDetailPage() {
  const { documentId } = Route.useParams()
  const router = useRouter()
  const { theme: rawTheme } = useTheme()
  const theme = rawTheme === "system"
    ? (window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light")
    : rawTheme
  const { data: doc, isLoading, isError, isPlaceholderData } = useDocument(documentId)
  const deleteDoc = useDeleteDocument()
  const patchDoc = usePatchDocument()
  const { data: rooms } = useRooms()

  // Title: null = show doc.title, string = user is editing
  const [titleDraft, setTitleDraft] = useState<string | null>(null)
  // Tags and room: local state, auto-saved on change
  const [tags, setTags] = useState<string[]>([])
  const [roomId, setRoomId] = useState<string | null>(null)
  const [contentDirty, setContentDirty] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)

  const editor = useCreateBlockNote({ schema: editorSchema })

  // Suppresses onChange during programmatic replaceBlocks so init doesn't
  // mark the content dirty. replaceBlocks fires onChange synchronously.
  const suppressDirtyRef = useRef(false)

  // Initialise local state once per document. Re-runs when documentId changes
  // (navigation to a different doc) but not on cache re-fetches, so user edits
  // are never silently overwritten while they're working.
  const initKeyRef = useRef<string | null>(null)
  useEffect(() => {
    if (!doc || isPlaceholderData) return
    if (initKeyRef.current === documentId) return
    initKeyRef.current = documentId
    setTitleDraft(null)
    setTags([...new Set([...doc.auto_tags, ...doc.user_tags])])
    setRoomId(doc.room_id ?? null)
    setContentDirty(false)
    suppressDirtyRef.current = true
    editor.replaceBlocks(editor.document, editor.tryParseMarkdownToBlocks(doc.content ?? ""))
    suppressDirtyRef.current = false
  }, [documentId, doc, isPlaceholderData, editor])

  const onClose = () => router.history.back()

  if (isLoading) {
    return <div className="p-6 text-sm text-muted-foreground">Loading...</div>
  }

  if (isError || !doc) {
    return (
      <div className="p-6 flex flex-col gap-4 w-full h-full">
        <Button variant="ghost" size="sm" className="self-start gap-1.5" onClick={onClose}>
          <ChevronLeft className="w-4 h-4" /> Back to Mind Palace
        </Button>
        <div className="flex flex-col gap-2 pt-4">
          <Empty>
            <CircleAlertIcon />
            <EmptyTitle>Document Not Found</EmptyTitle>
            <EmptyDescription>This document may have been deleted.</EmptyDescription>
          </Empty>
        </div>
      </div>
    )
  }

  const handleDelete = () => {
    deleteDoc.mutate(documentId, {
      onSuccess: () => {
        onClose()
        toast.success("Deleted")
      },
      onError: (e) => {
        setConfirmDelete(false)
        toast.error(e.message)
      },
    })
  }

  const handleTitleBlur = () => {
    if (titleDraft === null || titleDraft === doc.title) {
      setTitleDraft(null)
      return
    }
    patchDoc.mutate(
      { id: documentId, patch: { title: titleDraft || null } },
      {
        onSuccess: () => setTitleDraft(null),
        onError: (e) => toast.error(e.message),
      },
    )
  }

  const handleRoomChange = (value: string | null) => {
    const newRoomId = value || null
    setRoomId(newRoomId)
    patchDoc.mutate(
      { id: documentId, patch: { room_id: newRoomId } },
      {
        onError: (e) => {
          setRoomId(doc.room_id ?? null)
          toast.error(e.message)
        },
      },
    )
  }

  const handleTagsChange = (newTags: string[]) => {
    setTags(newTags)
    const newAutoTags = newTags.filter((t) => doc.auto_tags.includes(t))
    const newUserTags = newTags.filter((t) => !doc.auto_tags.includes(t))
    patchDoc.mutate(
      { id: documentId, patch: { auto_tags: newAutoTags, user_tags: newUserTags } },
      {
        onError: (e) => {
          setTags([...new Set([...doc.auto_tags, ...doc.user_tags])])
          toast.error(e.message)
        },
      },
    )
  }

  const handleContentSave = async () => {
    const markdown = await editor.blocksToMarkdownLossy(editor.document)
    patchDoc.mutate(
      { id: documentId, patch: { content: markdown } },
      {
        onSuccess: () => {
          setContentDirty(false)
          toast.success("Saved")
        },
        onError: (e) => toast.error(e.message),
      },
    )
  }

  const displayTitle = titleDraft ?? doc.title ?? ""

  return (
    <div className="flex flex-col gap-8 overflow-y-auto h-full pr-4 pb-12 w-full">
      <div>
        <div className="flex flex-row items-center gap-2 text-xs font-medium tracking-widest uppercase mb-6">
          <Button variant="ghost" size="icon" onClick={onClose}>
            <ChevronLeft />
          </Button>
          <div className="flex flex-row items-center text-muted-foreground">
            <span>MIND PALACE</span>
            <Dot />
            <span>{CONTENT_TYPE_LABELS[doc.content_type] ?? doc.content_type}</span>
          </div>
        </div>

        <div className="flex items-start gap-3 mb-3">
          <input
            className="flex-1 text-3xl font-medium bg-transparent border-b border-transparent outline-none pb-1 hover:border-muted-foreground/20 focus:border-primary/50 transition-colors"
            style={{ viewTransitionName: `doc-title-${documentId}` }}
            value={displayTitle}
            onChange={(e) => setTitleDraft(e.target.value)}
            onBlur={handleTitleBlur}
            placeholder="Untitled"
          />
          <div className="flex items-center gap-1 pt-1 shrink-0">
            {contentDirty && (
              <Button size="sm" onClick={handleContentSave} disabled={patchDoc.isPending}>
                {patchDoc.isPending ? "Saving…" : "Save"}
              </Button>
            )}
            <Button
              size="sm"
              variant="ghost"
              className="gap-1.5"
              onClick={() =>
                patchDoc.mutate({ id: documentId, patch: { is_pinned: !doc.is_pinned } })
              }
              disabled={isPlaceholderData || patchDoc.isPending}
            >
              <Pin className={cn("w-3.5 h-3.5", doc.is_pinned && "fill-current")} />
              {doc.is_pinned ? "Pinned" : "Pin"}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="gap-1.5 text-destructive hover:text-destructive"
              onClick={() => setConfirmDelete(true)}
              disabled={isPlaceholderData}
            >
              <Trash2 className="w-3.5 h-3.5" /> Delete
            </Button>
          </div>
        </div>

        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          {doc.source_url && (
            <>
              <span className="truncate max-w-[200px]">{new URL(doc.source_url).hostname}</span>
              <Dot />
            </>
          )}
          <span>Added {formatDistanceToNow(new Date(doc.created_at), { addSuffix: true })}</span>
          <Dot />
          <span>
            {doc.chunk_count} {doc.chunk_count === 1 ? "chunk" : "chunks"}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[4fr_1fr] gap-8 items-start">
        <div className="flex flex-col gap-8">
          {/* AI Summary — always visible */}
          {doc.summary && (
            <div className="rounded-xl border border-primary/20 bg-primary/5 p-5">
              <h3 className="text-xs font-semibold uppercase tracking-widest text-primary/70 mb-3">
                MIMIR'S SUMMARY
              </h3>
              <p
                className="text-sm leading-relaxed text-foreground"
                style={{ viewTransitionName: `doc-summary-${documentId}` }}
              >
                {doc.summary}
              </p>
            </div>
          )}

          {/* Content editor — always mounted, Save button appears when dirty */}
          {isPlaceholderData ? (
            <div className="flex flex-col gap-3">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-[92%]" />
              <Skeleton className="h-4 w-[96%]" />
              <Skeleton className="h-4 w-[88%]" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-[75%]" />
              <div className="mt-4" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-[94%]" />
              <Skeleton className="h-4 w-[80%]" />
            </div>
          ) : (
            <div className="overflow-hidden min-h-[300px]">
              <BlockNoteView
                editor={editor}
                theme={theme}
                formattingToolbar={false}
                onChange={() => {
                  if (!suppressDirtyRef.current) setContentDirty(true)
                }}
              >
                <EditorFormattingToolbar />
              </BlockNoteView>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="flex flex-col gap-6">
          <div className="rounded-lg border bg-card p-5">
            <h3 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-4">
              METADATA
            </h3>
            <div className="flex flex-col gap-3 text-sm">
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Kind</span>
                <span className="font-medium capitalize">
                  {CONTENT_TYPE_LABELS[doc.content_type] ?? doc.content_type}
                </span>
              </div>
              {doc.source_url && (
                <div className="flex justify-between items-center gap-4">
                  <span className="text-muted-foreground shrink-0">Source</span>
                  <a
                    href={doc.source_url}
                    target="_blank"
                    rel="noreferrer"
                    className="font-medium hover:underline truncate max-w-[150px]"
                  >
                    {new URL(doc.source_url).hostname}
                  </a>
                </div>
              )}
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Added</span>
                <span className="font-medium">{new Date(doc.created_at).toLocaleDateString()}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Words</span>
                <span className="font-medium">
                  {doc.content?.length ? Math.round(doc.content.length / 5).toLocaleString() : 0}
                </span>
              </div>
            </div>
          </div>

          <div className="rounded-lg border bg-card p-5">
            <h3 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-4">
              ROOM
            </h3>
            <Select value={roomId ?? ""} onValueChange={handleRoomChange}>
              <SelectTrigger>
                <SelectValue placeholder="Unassigned">
                  {roomId
                    ? (rooms?.find((r) => r.id === roomId)?.name ?? "Unassigned")
                    : "Unassigned"}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="" className="text-muted-foreground">
                  Unassigned
                </SelectItem>
                {rooms?.map((r) => (
                  <SelectItem key={r.id} value={r.id}>
                    {r.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="rounded-lg border bg-card p-5">
            <h3 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-4">
              TAGS
            </h3>
            <TagInput tags={tags} onChange={handleTagsChange} />
          </div>
        </div>
      </div>

      <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete document?</DialogTitle>
            <DialogDescription>
              "{doc.title || "Untitled"}" will be permanently deleted. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmDelete(false)}
              disabled={deleteDoc.isPending}
            >
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleteDoc.isPending}>
              {deleteDoc.isPending ? "Deleting…" : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
